import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * The live count COA's own "Pending Review" Overview fact shows (found live: a QA reviewer wants to see how
 * much is waiting before opening the queue, not just a static shortcut to it), as the framework's `PortalFigure`
 * (FLF-S88): the number, which opens the first pending reviews in a drawer and links to the Pending list.
 *
 * Mounted as one of the manifest's own `overviewSection.facts` (`PortalModuleOverviewFact.content` in
 * `@equerranz/portal-module-contract`, a real, module-owned `ReactElement` the framework renders as-is). This component
 * resolves its own transport and runs its own query, exactly as a full page does, because the framework's Overview
 * section has no data of its own to hand it.
 *
 * The value is the endpoint's `totalCount` (the whole total, not the first page's row count). It reads "Loading" while
 * the read is in flight and "Unavailable" when it fails, as the framework's own figures do.
 * The manifest marks the fact `interactive`, so the shell draws a plain card rather than a link and the figure opens its drawer.
 */
import { usePortalModuleLocation, usePortalModuleTransport } from '@equerranz/portal-module-contract';
import { PortalDateTime, PortalFigure, PortalListView, PortalStack, PortalText, } from '@equerranz/portal-ui';
import { useQuery } from '@tanstack/react-query';
import { COA_SLUG } from './acceptanceReferences.js';
import { pendingCoaReviewsQueryOptions } from './coaPendingReviews.js';
/** How many rows the drawer shows before it points at the full list. */
const DRAWER_ROW_LIMIT = 10;
const PENDING_PATH = 'reviews/pending';
const COLUMNS = [
    { columnId: 'fileName', header: 'File', renderCell: (entry) => entry.fileName ?? entry.fileKey },
    { columnId: 'receivedAt', header: 'Received', renderCell: (entry) => _jsx(PortalDateTime, { value: entry.receivedAt }) },
];
export function CoaPendingReviewCountBadge() {
    const transport = usePortalModuleTransport(COA_SLUG);
    const location = usePortalModuleLocation(COA_SLUG);
    const pending = useQuery(pendingCoaReviewsQueryOptions(transport));
    const total = pending.data === undefined ? 0 : Number(pending.data.totalCount ?? pending.data.pendingReviews.length);
    return (_jsx(PortalFigure, { label: "Pending Review", value: pending.isError ? 'Unavailable' : pending.data === undefined ? undefined : total, drilldown: {
            title: 'Pending reviews',
            records: () => (_jsxs(PortalStack, { children: [_jsx(PortalListView, { ariaLabel: "Pending reviews", columns: COLUMNS, rows: pending.data?.pendingReviews.slice(0, DRAWER_ROW_LIMIT) ?? [], getRowId: (entry) => entry.id, emptyMessage: "No COA PDFs are waiting for review." }), total > DRAWER_ROW_LIMIT ? (_jsxs(PortalText, { typeRole: "body", as: "p", children: ["Showing ", DRAWER_ROW_LIMIT, " of ", total] })) : null] })),
            fullListHref: location.hrefOf(PENDING_PATH),
            // The href starts at the module root and `navigate` takes a path relative to it, so the href is not passed on.
            onNavigate: () => {
                location.navigate(PENDING_PATH);
            },
        } }));
}
export default CoaPendingReviewCountBadge;
//# sourceMappingURL=CoaPendingReviewCountBadge.js.map